import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { GetSubscriptionResponse } from '@upright/common/subscription/api-types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';

/**
 * Hook to get the subscription tier for a specific company.
 * For DMA Consultant organizations, the tier is derived from company payment status.
 * For DMA Freemium and other organizations, returns the organization-level subscription.
 *
 * @param companyId - The company to fetch subscription for
 * @param enabled - Optional flag to conditionally enable the query (default: true).
 *   Pass false for non-consultant users to avoid unnecessary API requests.
 */
export const useGetCompanySubscription = (
  companyId: string,
  enabled = true,
): UseQueryResult<GetSubscriptionResponse, Error> => {
  const user = useGetLoggedInUser();

  return useQuery<GetSubscriptionResponse>({
    queryKey: ['get-company-subscription', companyId, user?.id],
    queryFn: async () => {
      return await fetchOrThrow<GetSubscriptionResponse>(`/subscription/company/${companyId}`, 'GET');
    },
    enabled: !!user && !!companyId && enabled,
  });
};
